import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
    Box,
    Typography,
    TextField,
    Button,
    List,
    ListItem,
    IconButton,
    FormHelperText,
    Paper,
    FormLabel
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';


const ResourcesSection = ({ onNext, onBack, isFirstSection, formData }) => {
    const { control, handleSubmit, formState: { errors }, setValue, watch, reset } = useForm({
        defaultValues: {
            zasoby_wstep_pytanie: formData?.zasoby_wstep_pytanie || '',
            zasoby_lista: formData?.zasoby_lista || [],
            zasoby_brakujace: formData?.zasoby_brakujace || ''
        }
    });

    // Update form values when formData changes (when user goes back)
    useEffect(() => {
        if (formData) {
            reset({
                zasoby_wstep_pytanie: formData?.zasoby_wstep_pytanie || '',
                zasoby_lista: formData?.zasoby_lista || [],
                zasoby_brakujace: formData?.zasoby_brakujace || ''
            });
        }
    }, [formData, reset]);

    const [currentStep, setCurrentStep] = useState(() => {
        // Determine which step to show based on completed data
        // If all data is complete, show the first step so user can review
        if (formData?.zasoby_wstep_pytanie && formData?.zasoby_lista?.length >= 4 && formData?.zasoby_brakujace) {
            return 2;
        }

        return 0;
    });
    const [newResource, setNewResource] = useState('');
    const [firstStep, setFirstStep] = useState(formData?.zasoby_wstep_pytanie || '');
    const [thirdStep, setThirdStep] = useState(formData?.zasoby_brakujace || '');
    const [editingIndex, setEditingIndex] = useState(null);
    const [editedValue, setEditedValue] = useState('');
    const resources = watch('zasoby_lista');

    // Sync local state with formData when user returns to this section
    useEffect(() => {
        if (formData) {
            setFirstStep(formData?.zasoby_wstep_pytanie || '');
            setThirdStep(formData?.zasoby_brakujace || '');
        }
    }, [formData]);

    const handleAddResource = () => {
        if (newResource.trim() && resources.length < 5) {
            setValue('zasoby_lista', [...resources, newResource.trim()]);
            setNewResource('');
        }
    };

    const handleRemoveResource = (index) => {
        setValue('zasoby_lista', resources.filter((_, i) => i !== index));
    };

    const handleEditResource = (index) => {
        setEditingIndex(index);
        setEditedValue(resources[index]);
    };

    const handleSaveEdit = () => {
        if (editedValue.trim()) {
            const updatedResources = [...resources];
            updatedResources[editingIndex] = editedValue.trim();
            setValue('zasoby_lista', updatedResources);
            setEditingIndex(null);
            setEditedValue('');
        }
    };

    const handleCancelEdit = () => {
        setEditingIndex(null);
        setEditedValue('');
    };

    const handleNext = () => {
        if (currentStep < 2) {
            setCurrentStep(currentStep + 1);
        } else {
            handleSubmit(onNext)();
        }
    };

    const thoughts = (goal) => {
        switch (goal) {
            case "Szukam pierwszej pracy":
                return `Jakie Twoje cechy ułatwią Ci znalezienie pierwszej pracy?`;
            case "Szukam pracy":
                return `Jakie Twoje cechy ułatwią Ci znalezienie pracy?`;
            case "Myślę o znalezieniu innej/nowej pracy":
                return `Jakie Twoje cechy ułatwią Ci decyzję o szukaniu innej pracy?`;
            case "Wracam na rynek pracy":
                return `Jakie Twoje cechy ułatwią Ci powrót na rynek pracy?`;
            case "Przekwalifikowuję się":
                return `Jakie Twoje cechy ułatwią Ci przekwalifikowanie się?`;
            default:
                return "";
        }
    }

    const whatMore = (goal) => {
        switch (goal) {
            case "Szukam pierwszej pracy":
                return "Czego Ci jeszcze brakuje żeby ruszyć dalej?";// "Czego jeszcze potrzebujesz by znalezienie pierwszej pracy było możliwe?";
            case "Szukam pracy":
                return "Czego Ci jeszcze brakuje żeby ruszyć dalej?";//"Czego jeszcze potrzebujesz by znalezienie pracy było możliwe?";
            case "Myślę o znalezieniu innej/nowej pracy":
                return "Czego Ci jeszcze brakuje żeby ruszyć dalej?";// "Czego jeszcze potrzebujesz by decyzja o szukaniu innej pracy była możliwa?";
            case "Wracam na rynek pracy":
                return "Czego Ci jeszcze brakuje żeby ruszyć dalej?";//"Czego jeszcze potrzebujesz by powrót na rynek pracy był możliwy?";
            case "Przekwalifikowuję się":
                return "Czego Ci jeszcze brakuje żeby ruszyć dalej?";//"Czego jeszcze potrzebujesz by przekwalifikowanie się było możliwe?";
            default:
                return "";
        }
    }

    const isFirstStepDisabled = () => {
        return !firstStep.length;
    };

    const isSecondStepDisabled = () => {
        return resources.length < 4;
    };

    const isThirdStepDisabled = () => {
        return !thirdStep.length;
    };

    const isNextButtonDisabled = () => {
        if (currentStep === 0) {
            return isFirstStepDisabled();
        } else if (currentStep === 1) {
            return isSecondStepDisabled();
        } else if (currentStep === 2) {
            return isThirdStepDisabled();
        }
        return false;
    };

    const isAddResourceButtonDisabled = () => {
        return !newResource.trim() || resources.length >= 8;
    };

    const isResourceInputDisabled = () => {
        return resources.length >= 8;
    };

    return (
        <Box component="form" sx={{ mt: 3 }}>
            {currentStep === 0 && (
                <>
                    <FormLabel>
                        <Typography variant="h6" color="black" gutterBottom>
                            {thoughts(formData.cel_wybor)}
                        </Typography>
                    </FormLabel>
                    <Controller
                        name="zasoby_wstep_pytanie"
                        control={control}
                        rules={{ required: "To pole jest wymagane" }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                onChange={(e) => {
                                    setFirstStep(e.target.value); field.onChange(e.target.value)
                                    setValue('zasoby_wstep_pytanie', e.target.value)
                                }}
                                multiline
                                rows={3}
                                error={!!errors.zasoby_wstep_pytanie}
                                helperText={errors.zasoby_wstep_pytanie?.message}
                                sx={{ mb: 3 }}
                            />
                        )}
                    />
                </>
            )}

            {currentStep === 1 && (
                <>
                    <Typography variant="h6" color="black" gutterBottom>
                        Wymień po kolei 4 swoje umiejętności, które mogą Ci w tym pomóc?
                    </Typography>
                    <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                            <TextField
                                fullWidth
                                value={newResource}
                                onChange={(e) => setNewResource(e.target.value)}
                                placeholder="Wpisz zasób..."
                                disabled={isResourceInputDisabled()}
                            />
                            <Button
                                onClick={handleAddResource}
                                disabled={isAddResourceButtonDisabled()}
                                color="primary"
                            >
                                Dodaj odpowiedź
                            </Button>
                        </Box>
                        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                            <List>
                                {resources.map((resource, index) => (
                                    <ListItem
                                        key={index}
                                        secondaryAction={
                                            editingIndex === index ? (
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <IconButton
                                                        edge="end"
                                                        aria-label="save"
                                                        onClick={handleSaveEdit}
                                                        color="primary"
                                                    >
                                                        <CheckIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        edge="end"
                                                        aria-label="cancel"
                                                        onClick={handleCancelEdit}
                                                    >
                                                        <CancelIcon />
                                                    </IconButton>
                                                </Box>
                                            ) : (
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <IconButton
                                                        edge="end"
                                                        aria-label="edit"
                                                        onClick={() => handleEditResource(index)}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        edge="end"
                                                        aria-label="delete"
                                                        onClick={() => handleRemoveResource(index)}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Box>
                                            )
                                        }
                                    >
                                        {editingIndex === index ? (
                                            <TextField
                                                fullWidth
                                                value={editedValue}
                                                onChange={(e) => setEditedValue(e.target.value)}
                                                size="small"
                                                autoFocus
                                            />
                                        ) : (
                                            `${index + 1}. ${resource}`
                                        )}
                                    </ListItem>
                                ))}
                            </List>
                        </Paper>
                        {errors.zasoby_lista && (
                            <FormHelperText error>{errors.zasoby_lista.message}</FormHelperText>
                        )}
                    </Box>
                </>
            )}

            {currentStep === 2 && (
                <>
                    <FormLabel>
                        <Typography variant="h6" color="black" gutterBottom>
                            {whatMore(formData.cel_wybor)}
                        </Typography>
                    </FormLabel>
                    <Controller
                        name="zasoby_brakujace"
                        control={control}
                        rules={{ required: "To pole jest wymagane" }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                multiline
                                onChange={(e) => {
                                    setThirdStep(e.target.value); field.onChange(e.target.value)
                                    setValue('zasoby_brakujace', e.target.value)
                                }}
                                rows={3}
                                error={!!errors.zasoby_brakujace}
                                helperText={errors.zasoby_brakujace?.message}
                                sx={{ mb: 3 }}
                            />
                        )}
                    />
                </>
            )}

            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <IconButton
                    onClick={() => {
                        if (currentStep === 0) {
                            onBack();
                        } else {
                            setCurrentStep(currentStep - 1);
                        }
                    }}
                    disabled={isFirstSection}
                    sx={{
                        border: '1px solid',
                        borderColor: isFirstSection ? 'action.disabled' : 'primary.main',
                        borderRadius: 1
                    }}
                >
                    <ArrowBackIcon />
                </IconButton>
                <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={handleNext}
                    disabled={isNextButtonDisabled()}
                    endIcon={<ArrowForwardIcon />}
                >
                    Dalej
                </Button>
            </Box>
        </Box>
    );
};

export default ResourcesSection;